import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { conversationsApi } from "@/lib/api";
import { toast } from "sonner";
import { normalizeInboxQuickFilter, normalizeInboxChannel } from "@/lib/inboxChannels";
import {
  applyOpenConversationUnreadClear,
  decrementInboxUnread,
  markConversationReadInList,
  resolveInboxSelectFirst,
  sameConversationId,
  stripInboxSelectParam,
  waitForDraftGeneration,
} from "@/lib/inboxHelpers";

function isRetryableDetailError(e, { silent, attempt }) {
  if (silent || attempt >= 1) return false;
  return e?.code === "network_error"
    || String(e?.message || "").toLowerCase().includes("failed to fetch");
}

function shouldClearDetailLoading(silent, clearLoading, requestId, currentRequestId) {
  return !silent && clearLoading && requestId === currentRequestId;
}

async function _processLoadDetailError({
  e,
  silent,
  attempt,
  requestId,
  detailRequestIdRef,
  pendingScrollTopRef,
  loadDetail,
  id,
  background,
  markReadOpt,
  setActiveConv,
  setActiveMessages,
  setDraftGenerating,
}) {
  if (requestId !== detailRequestIdRef.current) return { retry: false, clearLoading: true };

  if (isRetryableDetailError(e, { silent, attempt })) {
    await new Promise((resolve) => { setTimeout(resolve, 1500); });
    await loadDetail(id, { silent, background, attempt: attempt + 1, markRead: markReadOpt });
    return { retry: true, clearLoading: false };
  }

  pendingScrollTopRef.current = null;
  if (!silent) {
    toast.error(e?.message || "Could not load conversation");
    setActiveConv(null);
    setActiveMessages([]);
    setDraftGenerating(false);
  }
  return { retry: false, clearLoading: true };
}

function _monitorDraftGeneration({
  r,
  silent,
  id,
  draftWaitersRef,
  setGeneratingDrafts,
  loadDetail,
}) {
  const genStatus = r.draft_generation?.status;
  // Show the loader whenever the backend reports a live compose job — including
  // silent polls (inbound auto-draft while the thread is already open).
  if (genStatus === "generating") {
    setGeneratingDrafts((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });

    if (draftWaitersRef.current.has(id)) {
      return;
    }
    
    // Create a unique wait ID for this specific waiter loop
    const waitId = Date.now() + Math.random();
    draftWaitersRef.current.set(id, waitId);
    
    waitForDraftGeneration(conversationsApi, id, r.draft_generation)
      .then(async (result) => {
        // Only cancel if a newer waiter took over for this conversation
        if (draftWaitersRef.current.get(id) !== waitId) return;
        if (result?.status === "ready") {
          await loadDetail(id, { silent: true, background: true, markRead: false });
        }
      })
      .catch((e) => {
        if (draftWaitersRef.current.get(id) !== waitId) return;
        if (e?.code === "draft_generation_failed" || e?.code === "draft_generation_timeout") {
          toast.error(e.message || "Draft generation failed");
        }
      })
      .finally(() => {
        if (draftWaitersRef.current.get(id) === waitId) {
          draftWaitersRef.current.delete(id);
          setGeneratingDrafts((prev) => {
            if (!prev.has(id)) return prev;
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        }
      });
    return;
  }

  // Only clear on an explicit (non-silent) detail load. Silent polls must not
  // clear a loader owned by Ask-to-draft / an in-flight waiter.
  if (!silent) {
    setGeneratingDrafts((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }
}

export function useInboxData() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeChannel = searchParams.get("channel") || "all";
  const activeFilter = normalizeInboxQuickFilter(searchParams.get("filter"));
  const inboxQuery = searchParams.get("q") || "";
  const selectFirst = searchParams.get("select") === "first";

  const [search, setSearch] = useState(inboxQuery);
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [list, setList] = useState([]);
  const [listTotal, setListTotal] = useState(0);
  const [inboxSummary, setInboxSummary] = useState(null);
  const [listLoading, setListLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selectedId, setSelectedId] = useState(conversationId ?? null);
  const [activeConv, setActiveConv] = useState(null);
  const [activeMessages, setActiveMessages] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [generatingDrafts, setGeneratingDrafts] = useState(new Set());

  const threadScrollerRef = useRef(null);
  const pendingScrollTopRef = useRef(null);
  const prevFirstMessageIdRef = useRef(null);
  const detailRequestIdRef = useRef(0);
  /** Map of conversation id -> wait ID for independent draft polling. */
  const draftWaitersRef = useRef(new Map());
  /** Last known message_count for the open thread — bump triggers silent detail refresh. */
  const openMessageCountRef = useRef(null);
  /** Conversation IDs successfully marked read in this session (lowercase). */
  const markedReadIdsRef = useRef(new Set());
  /** Subset of markedReadIdsRef where POST /read (or GET mark_read) confirmed. */
  const confirmedReadIdsRef = useRef(new Set());
  const conversationIdRef = useRef(conversationId);

  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  useEffect(() => {
    setSelectedId(conversationId ?? null);
  }, [conversationId]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const current = next.get("q") || "";
    if (debouncedSearch === current) return;
    if (debouncedSearch) next.set("q", debouncedSearch);
    else next.delete("q");
    setSearchParams(next, { replace: true });
  }, [debouncedSearch, searchParams, setSearchParams]);

  const setActiveChannel = useCallback((channelId) => {
    const next = stripInboxSelectParam(searchParams);
    if (channelId === "all") next.delete("channel");
    else next.set("channel", channelId);
    
    // If the active conversation doesn't match the new channel, clear it
    if (activeConv && channelId !== "all") {
      const convChannel = normalizeInboxChannel(activeConv.channel);
      const targetChannel = normalizeInboxChannel(channelId);
      if (convChannel !== targetChannel) {
        navigate(next.toString() ? `/inbox?${next.toString()}` : "/inbox", { replace: true });
        return;
      }
    }
    
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, activeConv, navigate]);

  const setActiveFilter = useCallback((filterId) => {
    const next = stripInboxSelectParam(searchParams);
    if (filterId === "all") next.delete("filter");
    else next.set("filter", filterId);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const navigateToConversation = useCallback((id) => {
    const qs = stripInboxSelectParam(searchParams).toString();
    navigate(qs ? `/inbox/${id}?${qs}` : `/inbox/${id}`);
  }, [navigate, searchParams]);

  const isSelectedConversation = useCallback((id) => (
    sameConversationId(selectedId, id) || sameConversationId(conversationId, id)
  ), [selectedId, conversationId]);

  const selectConversation = useCallback((id) => {
    setSelectedId(id);
    navigateToConversation(id);
  }, [navigateToConversation]);

  const acknowledgeRead = useCallback((id, channel) => {
    if (!id) return;
    const key = String(id).toLowerCase();
    const alreadyMarked = markedReadIdsRef.current.has(key);
    markedReadIdsRef.current.add(key);

    let rowChannel = channel;
    let openRowFound = false;

    setList((prev) => {
      const result = markConversationReadInList(prev, id);
      if (result.openRow) {
        openRowFound = true;
        if (!rowChannel) rowChannel = result.openRow.channel;
      }
      return result.conversations;
    });

    if (!alreadyMarked && openRowFound) {
      setInboxSummary((summary) => decrementInboxUnread(summary, rowChannel));
    }
  }, []);

  const markConversationRead = useCallback(async (id, { background = false } = {}) => {
    if (!id) return false;
    try {
      await conversationsApi.markRead(id, { background });
      const key = String(id).toLowerCase();
      markedReadIdsRef.current.add(key);
      confirmedReadIdsRef.current.add(key);
      acknowledgeRead(id);
      return true;
    } catch (e) {
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.warn("inbox mark-read failed", e?.message);
      }
      return false;
    }
  }, [acknowledgeRead]);

  const clearConversation = useCallback(() => {
    const leavingId = conversationId || selectedId;
    if (leavingId) {
      // Flush read state when leaving the thread without sending a reply.
      markConversationRead(leavingId, { background: true });
    }
    setSelectedId(null);
    setActiveConv(null);
    setActiveMessages([]);
    const qs = stripInboxSelectParam(searchParams).toString();
    navigate(qs ? `/inbox?${qs}` : "/inbox");
  }, [navigate, searchParams, conversationId, selectedId, markConversationRead]);

  const syncChannel = useCallback(async () => {
    setSyncing(true);
    try {
      const r = await conversationsApi.syncInbox({
        channel: activeChannel !== "all" ? activeChannel : undefined,
      });
      if (r.inbox_summary) setInboxSummary(r.inbox_summary);
      return r;
    } catch (e) {
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.warn("inbox sync failed", e?.message);
      }
      return null;
    } finally {
      setSyncing(false);
    }
  }, [activeChannel]);

  const refreshList = useCallback(async ({ sync = false, background = false } = {}) => {
    try {
      // Await Gmail/channel sync before listing so newly ingested mail appears
      // on this refresh instead of only on the next 15s poll.
      if (sync) {
        await syncChannel().catch(() => {});
      }
      const r = await conversationsApi.list({
        limit: 100,
        channel: activeChannel !== "all" ? activeChannel : undefined,
        q: inboxQuery || undefined,
        filter: activeFilter !== "all" ? activeFilter : undefined,
        background,
      });
      const incoming = r.conversations || [];
      let conversations = incoming;
      let summary = r.inbox_summary;
      const activeConvId = conversationIdRef.current;
      if (activeConvId) {
        const cleared = applyOpenConversationUnreadClear(
          incoming,
          summary,
          activeConvId,
          { forceClear: true },
        );
        conversations = cleared.conversations;
        if (cleared.summary) summary = cleared.summary;
      } else if (markedReadIdsRef.current.size) {
        // Keep session-opened threads clear after leaving until mark-read is
        // confirmed. A later unread from the server after confirmation means a
        // new inbound — restore the red dot and drop the session mark.
        conversations = incoming.map((c) => {
          const key = String(c.id).toLowerCase();
          if (!markedReadIdsRef.current.has(key)) return c;
          if (!c.is_unread) {
            confirmedReadIdsRef.current.add(key);
            return c;
          }
          if (confirmedReadIdsRef.current.has(key)) {
            markedReadIdsRef.current.delete(key);
            confirmedReadIdsRef.current.delete(key);
            return c;
          }
          return { ...c, is_unread: false };
        });
      }
      setList(conversations);
      setListTotal(r.total ?? conversations.length);
      if (summary) setInboxSummary(summary);
      if (r.assistant_name) {
        const { usePersonaStore } = await import("@/store/usePersonaStore");
        usePersonaStore.getState().hydrateFromServer({ assistant_name: r.assistant_name });
      }
      return conversations;
    } catch (e) {
      toast.error(e?.message || "Could not load conversations");
      return [];
    } finally {
      setListLoading(false);
    }
  }, [activeChannel, activeFilter, inboxQuery, syncChannel]);

  const loadDetail = useCallback(async (
    id,
    { silent = false, background = false, attempt = 0, markRead: markReadOpt } = {},
  ) => {
    if (!id) {
      setActiveConv(null);
      setActiveMessages([]);
      return;
    }
    const requestId = ++detailRequestIdRef.current;
    
    if (silent && threadScrollerRef.current) {
      pendingScrollTopRef.current = threadScrollerRef.current.scrollTop;
    }
    const tabVisible = typeof document === "undefined" || document.visibilityState === "visible";
    const shouldMark = markReadOpt !== undefined ? Boolean(markReadOpt) : (!silent || tabVisible);

    // Optimistic unread clear only — GET marks read once. Do not await a separate POST.
    if (!silent) {
      setDetailLoading(true);
      acknowledgeRead(id);
    } else if (shouldMark) {
      acknowledgeRead(id);
    }

    let clearLoading = true;

    try {
      const r = await conversationsApi.get(id, {
        background,
        markRead: shouldMark,
      });
      if (requestId !== detailRequestIdRef.current) return;
      
      setActiveConv(r.conversation);
      setActiveMessages(r.messages || []);
      if (shouldMark) {
        acknowledgeRead(id, r.conversation?.channel);
      }

      _monitorDraftGeneration({
        r,
        silent,
        id,
        draftWaitersRef,
        setGeneratingDrafts,
        loadDetail,
      });
    } catch (e) {
      const res = await _processLoadDetailError({
        e,
        silent,
        attempt,
        requestId,
        detailRequestIdRef,
        pendingScrollTopRef,
        loadDetail,
        id,
        background,
        markReadOpt,
        setActiveConv,
        setActiveMessages,
        setDraftGenerating,
      });
      clearLoading = res.clearLoading;
      if (res.retry) return;
    } finally {
      if (shouldClearDetailLoading(silent, clearLoading, requestId, detailRequestIdRef.current)) {
        setDetailLoading(false);
      }
    }
  }, [acknowledgeRead]);

  useEffect(() => {
    setListLoading(true);
    refreshList();
  }, [refreshList]);

  useEffect(() => {
    const timer = setInterval(() => { refreshList({ background: true }); }, 15000);
    return () => clearInterval(timer);
  }, [refreshList]);

  useEffect(() => {
    if (conversationId) return;
    setActiveConv(null);
    setActiveMessages([]);
  }, [conversationId]);

  const hasActiveFilters = activeFilter !== "all" || !!inboxQuery;

  // Today Pulse deep-link only: `?select=first` opens the latest matching row once.
  // Normal inbox filters / sidebar / conversation clicks never set this flag.
  useEffect(() => {
    const decision = resolveInboxSelectFirst({
      selectFirst,
      conversationId,
      listLoading,
      list,
    });
    if (decision.action === "none" || decision.action === "wait") return;

    const next = stripInboxSelectParam(searchParams);
    const qs = next.toString();
    if (decision.action === "scrub") {
      setSearchParams(next, { replace: true });
      return;
    }
    navigate(qs ? `/inbox/${decision.id}?${qs}` : `/inbox/${decision.id}`, { replace: true });
  }, [
    selectFirst,
    conversationId,
    listLoading,
    list,
    searchParams,
    navigate,
    setSearchParams,
  ]);

  useEffect(() => {
    if (!conversationId) return;
    if (activeChannel !== "all" && list.length === 0 && !hasActiveFilters) {
      setActiveConv(null);
      setActiveMessages([]);
      const qs = stripInboxSelectParam(searchParams).toString();
      navigate(qs ? `/inbox?${qs}` : "/inbox", { replace: true });
    }
  }, [conversationId, list, activeChannel, hasActiveFilters, navigate, searchParams]);

  useEffect(() => {
    if (!conversationId) return;
    loadDetail(conversationId);
  }, [conversationId, loadDetail]);

  useEffect(() => {
    openMessageCountRef.current = null;
  }, [conversationId]);

  // When the conversation list shows new messages on the open thread, refresh
  // detail immediately so inbound + auto-draft "generating" appear without a
  // full page reload (list polls every 15s; detail alone every 10s).
  useEffect(() => {
    if (!conversationId || !list.length) return;
    const row = list.find((c) => sameConversationId(c.id, conversationId));
    if (!row) return;
    const count = Number(row.message_count) || 0;
    const prev = openMessageCountRef.current;
    openMessageCountRef.current = count;
    if (prev != null && count > prev) {
      loadDetail(conversationId, { silent: true, background: true, markRead: false });
    }
  }, [list, conversationId, loadDetail]);

  useEffect(() => {
    if (!conversationId) return undefined;
    const timer = setInterval(() => {
      loadDetail(conversationId, { silent: true, background: true, markRead: false });
    }, 10000);
    return () => clearInterval(timer);
  }, [conversationId, loadDetail]);

  const maintainThreadScroll = useCallback((visibleMessages) => {
    const el = threadScrollerRef.current;
    if (!el) return;
    const firstId = visibleMessages[0]?.id ?? null;
    if (pendingScrollTopRef.current !== null) {
      el.scrollTop = pendingScrollTopRef.current;
      pendingScrollTopRef.current = null;
    } else if (
      firstId
      && prevFirstMessageIdRef.current
      && firstId !== prevFirstMessageIdRef.current
      && el.scrollTop < 80
    ) {
      el.scrollTop = 0;
    }
    prevFirstMessageIdRef.current = firstId;
  }, []);

  const globalEmpty = !listLoading
    && (inboxSummary?.total || 0) === 0
    && !(inboxSummary?.configured_channels?.length);

  return {
    conversationId,
    activeChannel,
    activeFilter,
    inboxQuery,
    hasActiveFilters,
    globalEmpty,
    search,
    setSearch,
    list,
    setList,
    listTotal,
    inboxSummary,
    listLoading,
    syncing,
    activeConv,
    setActiveConv,
    activeMessages,
    setActiveMessages,
    detailLoading,
    draftGenerating: activeConv?.id ? generatingDrafts.has(activeConv.id) : false,
    setDraftGenerating: (isGenerating) => {
      if (!activeConv?.id) return;
      setGeneratingDrafts((prev) => {
        const next = new Set(prev);
        if (isGenerating) next.add(activeConv.id);
        else next.delete(activeConv.id);
        return next;
      });
    },
    threadScrollerRef,
    setActiveChannel,
    setActiveFilter,
    isSelectedConversation,
    selectConversation,
    clearConversation,
    refreshList,
    loadDetail,
    maintainThreadScroll,
  };
}
